// Branding-logo + overlay rendering helpers for the admin order-template
// editor. Copied from frontend/src/utils/{brandingLogo,overlayImage}.js so
// the admin reproduces the customer's invitation pixel-for-pixel. Keep in sync.
import { fabric } from 'fabric';

export const BRANDING_DEFAULTS = { xPercent: 3, yPercent: 50, rotation: -90, scalePercent: 8 };
export const OVERLAY_DEFAULTS = { xPercent: 50, yPercent: 50, rotation: 0, scalePercent: 30 };

export function tintImage(url, color) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = color || '#FFFFFF';
      ctx.fillRect(0, 0, c.width, c.height);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
}

export async function addBrandingLogoToCanvas(canvas, imageUrl, color, naturalW, naturalH, position = {}) {
  if (!imageUrl) return null;
  const existing = canvas.getObjects().find(o => o._isBrandingLogo);
  if (existing) canvas.remove(existing);

  const xPercent = position.xPercent ?? BRANDING_DEFAULTS.xPercent;
  const yPercent = position.yPercent ?? BRANDING_DEFAULTS.yPercent;
  const rotation = position.rotation ?? BRANDING_DEFAULTS.rotation;
  const scalePercent = position.scalePercent ?? BRANDING_DEFAULTS.scalePercent;

  const tintedUrl = await tintImage(imageUrl, color);
  return new Promise(resolve => {
    fabric.Image.fromURL(tintedUrl, (img) => {
      const targetWidth = (scalePercent / 100) * naturalW;
      const scale = img.width ? targetWidth / img.width : 1;
      img.set({
        left: (xPercent / 100) * naturalW,
        top: (yPercent / 100) * naturalH,
        originX: 'center', originY: 'center',
        scaleX: scale, scaleY: scale,
        angle: rotation, opacity: 1,
        selectable: false, evented: false,
      });
      img._isBrandingLogo = true;
      canvas.add(img);
      canvas.sendToBack(img);
      canvas.requestRenderAll();
      resolve(img);
    }, { crossOrigin: 'anonymous' });
  });
}

export async function addOverlayToCanvas(canvas, config, naturalW, naturalH, overlayId = 'main') {
  if (!config?.url) return null;
  const existing = canvas.getObjects().find(o => o._isOverlayImage && o._overlayId === overlayId);
  if (existing) canvas.remove(existing);

  const xPercent = config.xPercent ?? OVERLAY_DEFAULTS.xPercent;
  const yPercent = config.yPercent ?? OVERLAY_DEFAULTS.yPercent;
  const rotation = config.rotation ?? OVERLAY_DEFAULTS.rotation;
  const scalePercent = config.scalePercent ?? OVERLAY_DEFAULTS.scalePercent;

  const sourceUrl = config.color ? await tintImage(config.url, config.color) : config.url;

  return new Promise(resolve => {
    fabric.Image.fromURL(sourceUrl, (img) => {
      const targetWidth = (scalePercent / 100) * naturalW;
      const scale = img.width ? targetWidth / img.width : 1;
      img.set({
        left: (xPercent / 100) * naturalW,
        top: (yPercent / 100) * naturalH,
        originX: 'center', originY: 'center',
        scaleX: scale, scaleY: scale,
        angle: rotation, opacity: 1,
        selectable: false, evented: false,
      });
      img._isOverlayImage = true;
      img._overlayId = overlayId;
      canvas.add(img);
      if (canvas.getObjects().some(o => o._isBrandingLogo && o !== img)) {
        const logo = canvas.getObjects().find(o => o._isBrandingLogo);
        const logoIdx = canvas.getObjects().indexOf(logo);
        canvas.moveTo(img, logoIdx + 1);
      } else {
        canvas.sendToBack(img);
      }
      canvas.requestRenderAll();
      resolve(img);
    }, { crossOrigin: 'anonymous' });
  });
}
